import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from "react-toastify";
import { FaTimes, FaSpa, FaSpinner, FaPlus, FaTrash, FaChalkboardTeacher, FaUserMd } from 'react-icons/fa';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { AVAILABLE_SERVICES } from '../../utils/serviceConstants';

const servicesOptions = AVAILABLE_SERVICES;

const BecomeBusinessPartner = ({ onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationSchema = Yup.object({
    services: Yup.array()
      .of(
        Yup.object({
          service: Yup.string()
            .oneOf(servicesOptions, 'Please select a valid service type')
            .required('Service is required'),
          partnerType: Yup.string()
            .oneOf(['instructor', 'consultant'], 'Please select a partner type')
            .required('Partner type is required'),
          yearsOfExperience: Yup.number()
            .typeError('Years of experience must be a number')
            .min(0, 'Years of experience cannot be negative')
            .max(50, 'Years of experience cannot exceed 50')
            .integer('Years of experience must be a whole number')
            .required('Years of experience is required'),
          certification: Yup.string()
            .max(500, 'Certification must be at most 500 characters')
            .matches(/^[a-zA-Z0-9\s.,-]*$/, 'Certification can only contain letters, numbers, spaces, commas, periods, and hyphens'),
          bio: Yup.string()
            .max(500, 'Bio must be at most 500 characters'),
          teachingStyle: Yup.string()
            .max(200, 'Teaching style must be at most 200 characters'),
        })
      )
      .min(1, 'Please add at least one service')
      .max(2, 'You can only add a maximum of 2 services')
      .test('one-instructor-one-consultant', 'You must have exactly one service as Instructor and one as Consultant', function(value) {
        if (!value || value.length === 0) return true; // Let min validation handle empty case
        if (value.length === 1) {
          // If only one service, allow either type
          return true;
        }
        if (value.length === 2) {
          const partnerTypes = value.map(s => s.partnerType).filter(Boolean);
          const hasInstructor = partnerTypes.includes('instructor');
          const hasConsultant = partnerTypes.includes('consultant');
          // Must have exactly one of each
          return hasInstructor && hasConsultant && partnerTypes.length === 2;
        }
        return false;
      })
      .required('Services are required'),
    achievements: Yup.string()
      .max(1000, 'Achievements must be at most 1000 characters'),
  });

  const handleSubmit = async (values, { setErrors }) => {
    try {
      setIsSubmitting(true);
      
        // Transform services array into the format backend expects
        // Each service has its own details (experience, certification, bio, teachingStyle)
        // Parse achievements from textarea (one per line or comma-separated)
        let achievementsArray = [];
        if (values.achievements && values.achievements.trim()) {
          achievementsArray = values.achievements
            .split(/[,\n]/)
            .map(a => a.trim())
            .filter(a => a.length > 0);
        }
        
        let payload = {
          languages: values.languages || [],
          achievements: achievementsArray,
        };

      // Always use the new format with services array and servicePartnerTypes object
      // Backend will handle backward compatibility if needed
      if (values.services && values.services.length > 0) {
        payload.services = values.services.map(s => s.service).filter(Boolean);
        const servicePartnerTypes = {};
        const serviceDetails = {}; // Store per-service details
        
        values.services.forEach(s => {
          if (s.service) {
            servicePartnerTypes[s.service] = s.partnerType;
            // Store service-specific details
            serviceDetails[s.service] = {
              yearsOfExperience: s.yearsOfExperience || 0,
              certification: s.certification || '',
              bio: s.bio || '',
              teachingStyle: s.teachingStyle || ''
            };
          }
        });
        
        payload.servicePartnerTypes = servicePartnerTypes;
        payload.serviceDetails = serviceDetails; // Send per-service details
        
        // For backward compatibility, use first service's details as primary
        if (values.services.length > 0 && values.services[0].service) {
          const firstService = values.services[0];
          payload.service = firstService.service;
          payload.partnerType = firstService.partnerType;
          payload.yearsOfExperience = firstService.yearsOfExperience || 0;
          payload.certification = firstService.certification || '';
          payload.bio = firstService.bio || '';
          payload.teachingStyle = firstService.teachingStyle || '';
        }
        
        // Validate that all services have been selected
        if (payload.services.length === 0) {
          throw new Error('Please select at least one service');
        }
        if (payload.services.length > 2) {
          throw new Error('You can only add a maximum of 2 services');
        }
        if (payload.services.length !== Object.keys(payload.servicePartnerTypes).length) {
          throw new Error('Please select partner type for all services');
        }
        // Validate one instructor and one consultant if 2 services
        if (payload.services.length === 2) {
          const partnerTypes = Object.values(payload.servicePartnerTypes);
          const hasInstructor = partnerTypes.includes('instructor');
          const hasConsultant = partnerTypes.includes('consultant');
          if (!hasInstructor || !hasConsultant) {
            throw new Error('You must have exactly one service as Instructor and one as Consultant');
          }
        }
      } else {
        throw new Error('At least one service is required');
      }

      const { data } = await HttpInterceptor.post('/auth/become-trainer', payload);
      
      // Get success message
      const successMessage = data?.message || 'Application submitted successfully!';
      
      // Close the modal first, then show success message
      // This ensures the toast appears above everything else
      if (onSuccess) {
        await onSuccess();
      } else if (onClose) {
        onClose();
      }
      
      // Small delay to ensure modal is fully closed before showing toast
      setTimeout(() => {
        toast.success(successMessage);
      }, 100);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to submit application. Try again.';
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', zIndex: 9999 }}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative z-[10000]">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6 text-center relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <FaTimes className="text-2xl" />
          </button>
          <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaSpa className="text-3xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">Become a Business Partner</h2>
          <p className="text-white/80 mt-1">Register as a professional service provider</p>
        </div>

        {/* Form */}
        <Formik
          initialValues={{
            services: [{ 
              service: '', 
              partnerType: 'instructor',
              yearsOfExperience: 0,
              certification: '',
              bio: '',
              teachingStyle: ''
            }],
            languages: [],
            achievements: '' // Textarea for achievements
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => {
            const getAvailableServices = (currentIndex) => {
              const usedServices = values.services
                .map((s, idx) => idx !== currentIndex ? s.service : null)
                .filter(Boolean);
              return servicesOptions.filter(s => !usedServices.includes(s));
            };

            const addService = () => {
              // Check if already at max (2 services)
              if (values.services.length >= 2) {
                toast.error('You can only add a maximum of 2 services (one as Instructor and one as Consultant)');
                return;
              }
              
              // Determine default partner type based on existing services
              const existingPartnerTypes = values.services.map(s => s.partnerType).filter(Boolean);
              const defaultPartnerType = existingPartnerTypes.includes('instructor') 
                ? 'consultant' 
                : 'instructor';
              
              const newServices = [...values.services, { 
                service: '', 
                partnerType: defaultPartnerType,
                yearsOfExperience: 0,
                certification: '',
                bio: '',
                teachingStyle: ''
              }];
              setFieldValue('services', newServices);
            };

            const removeService = (index) => {
              if (values.services.length > 1) {
                const newServices = values.services.filter((_, i) => i !== index);
                setFieldValue('services', newServices);
              } else {
                toast.error('At least one service is required');
              }
            };

            return (
              <Form className="p-6 space-y-4">
                <ErrorMessage name="submit" component="div" className="text-red-600 text-sm text-center" />

                {/* Services with Partner Types */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Services & Partner Types *</label>
                  <p className="text-xs text-gray-500 mb-3">
                    You can add up to 2 services: one as Instructor (group sessions) and one as Consultant (one-on-one appointments). For each service, select the appropriate partner type.
                  </p>
                  {values.services.length >= 2 && (
                    <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                      <strong>Note:</strong> Maximum of 2 services allowed. You must have one service as Instructor and one as Consultant.
                    </div>
                  )}
                  
                  {values.services.map((serviceItem, index) => (
                    <div key={index} className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Service {index + 1}</span>
                        {values.services.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeService(index)}
                            className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
                          >
                            <FaTrash className="text-xs" />
                            Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Service Selection */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Service Type *</label>
                          <Field
                            as="select"
                            name={`services.${index}.service`}
                            className="w-full border rounded-lg py-2 px-3 bg-white text-sm focus:ring-teal-500 focus:border-teal-500"
                          >
                            <option value="">Select service</option>
                            {getAvailableServices(index).map((s, i) => (
                              <option key={i} value={s}>{s}</option>
                            ))}
                            {/* Show current selected service even if it's used elsewhere */}
                            {serviceItem.service && !getAvailableServices(index).includes(serviceItem.service) && (
                              <option value={serviceItem.service}>{serviceItem.service}</option>
                            )}
                          </Field>
                          <ErrorMessage name={`services.${index}.service`} component="p" className="text-red-500 text-xs mt-1" />
                        </div>

                        {/* Partner Type Selection */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Partner Type *</label>
                          <Field
                            as="select"
                            name={`services.${index}.partnerType`}
                            className="w-full border rounded-lg py-2 px-3 bg-white text-sm focus:ring-teal-500 focus:border-teal-500"
                          >
                            <option value="instructor">Instructor (Group Sessions)</option>
                            <option value="consultant">Consultant (One-on-One Appointments)</option>
                          </Field>
                          <ErrorMessage name={`services.${index}.partnerType`} component="p" className="text-red-500 text-xs mt-1" />
                        </div>
                      </div>

                      {/* Partner Type Description */}
                      {serviceItem.partnerType === 'instructor' && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                          <div className="flex items-start gap-2">
                            <FaChalkboardTeacher className="mt-0.5 flex-shrink-0" />
                            <div>
                              <strong>Instructor:</strong> You'll conduct group sessions with multiple students at fixed times. Students enroll in your programs and attend scheduled classes.
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {serviceItem.partnerType === 'consultant' && (
                        <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-800">
                          <div className="flex items-start gap-2">
                            <FaUserMd className="mt-0.5 flex-shrink-0" />
                            <div>
                              <strong>Consultant:</strong> You'll offer one-on-one appointments. Members book specific time slots, which you'll approve. They pay after your approval.
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Service-specific fields */}
                      <div className="mt-4 pt-4 border-t border-gray-300">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Service Details</h4>
                        
                        {/* Years of Experience */}
                        <div className="mb-3">
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Years of Experience *</label>
                          <Field
                            name={`services.${index}.yearsOfExperience`}
                            type="number"
                            min="0"
                            className="w-full border rounded-lg py-2 px-3 bg-white text-sm focus:ring-teal-500 focus:border-teal-500"
                          />
                          <ErrorMessage name={`services.${index}.yearsOfExperience`} component="p" className="text-red-500 text-xs mt-1" />
                        </div>

                        {/* Certification */}
                        <div className="mb-3">
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Certification</label>
                          <Field
                            name={`services.${index}.certification`}
                            type="text"
                            placeholder="Enter your certifications for this service"
                            className="w-full border rounded-lg py-2 px-3 bg-white text-sm focus:ring-teal-500 focus:border-teal-500"
                          />
                          <ErrorMessage name={`services.${index}.certification`} component="p" className="text-red-500 text-xs mt-1" />
                        </div>

                        {/* Bio */}
                        <div className="mb-3">
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Bio</label>
                          <Field
                            as="textarea"
                            name={`services.${index}.bio`}
                            rows="3"
                            placeholder="Tell us about your expertise in this service"
                            className="w-full border rounded-lg py-2 px-3 bg-white text-sm focus:ring-teal-500 focus:border-teal-500"
                          />
                          <ErrorMessage name={`services.${index}.bio`} component="p" className="text-red-500 text-xs mt-1" />
                        </div>

                        {/* Teaching Style / Consultation Approach */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">
                            {serviceItem.partnerType === 'consultant' ? 'Consultation Approach' : 'Teaching Style'}
                          </label>
                          <Field
                            name={`services.${index}.teachingStyle`}
                            type="text"
                            placeholder={
                              serviceItem.partnerType === 'consultant' 
                                ? "Describe your consultation approach and methodology"
                                : "Describe your teaching approach for this service"
                            }
                            className="w-full border rounded-lg py-2 px-3 bg-white text-sm focus:ring-teal-500 focus:border-teal-500"
                          />
                          <ErrorMessage name={`services.${index}.teachingStyle`} component="p" className="text-red-500 text-xs mt-1" />
                        </div>
                      </div>
                    </div>
                  ))}

                  {values.services.length < 2 && (
                    <button
                      type="button"
                      onClick={addService}
                      className="mt-2 text-sm text-teal-600 hover:text-teal-700 flex items-center gap-2 font-medium"
                    >
                      <FaPlus className="text-xs" />
                      Add Another Service {values.services.length === 1 && '(Max 2 services)'}
                    </button>
                  )}
                  {values.services.length >= 2 && (
                    <p className="mt-2 text-xs text-gray-500 italic">
                      Maximum of 2 services reached. You must have one as Instructor and one as Consultant.
                    </p>
                  )}

                  {/* Custom error display for services array */}
                  {errors.services && typeof errors.services === 'string' && (
                    <p className="text-red-500 text-xs mt-1">{errors.services}</p>
                  )}
                  {errors.services && typeof errors.services === 'object' && !Array.isArray(errors.services) && (
                    <p className="text-red-500 text-xs mt-1">Please fix the errors in the services above</p>
                  )}
                  {touched.services && Array.isArray(errors.services) && errors.services.some(err => err) && (
                    <p className="text-red-500 text-xs mt-1">Please complete all service selections</p>
                  )}
                </div>

                {/* Achievements (shared across all services) */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Achievements
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    List your professional achievements, awards, or notable accomplishments. Enter one per line or separate with commas.
                  </p>
                  <Field
                    as="textarea"
                    name="achievements"
                    placeholder="e.g., Certified Yoga Instructor (RYT-200), Winner of National Fitness Championship 2023, Published author on wellness topics"
                    rows="4"
                    className="w-full border rounded-lg py-2 px-3 bg-white text-sm focus:ring-teal-500 focus:border-teal-500"
                  />
                  <ErrorMessage name="achievements" component="p" className="text-red-500 text-xs mt-1" />
                </div>

              {/* Info Notice */}
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800 border border-blue-100">
                <p>
                  Your application will be reviewed by our admin team. You'll receive an email notification once your application is approved.
                </p>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-2 px-4 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:from-teal-700 hover:to-emerald-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaSpa className="text-white" />
                    <span>Submit Application</span>
                  </>
                )}
              </button>
            </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );

  // Render modal using portal to ensure it's at the root level
  return typeof document !== 'undefined' 
    ? createPortal(modalContent, document.body)
    : modalContent;
};

export default BecomeBusinessPartner;

